/**
 * /settings/telegram — founder Telegram bot configuration (Phase 11 / M9).
 *
 * Flow:
 *   1. Founder pastes a token from @BotFather + their Telegram user id.
 *   2. "Test connection" calls Telegram getMe through the bridge — no
 *      state change. Confirms the token is real and renders the bot
 *      identity (username / id) so the founder knows they pasted the
 *      right thing.
 *   3. "Register webhook" persists the token (Infisical via bridge) and
 *      calls setWebhook so Telegram starts delivering updates to
 *      https://chaos.01foundry.com/openclaw/telegram/webhook.
 *   4. "Disconnect" deletes the webhook and clears the in-process token.
 *
 * Authorization: route accepts any signed-in user; the gateway rejects
 * employee/tuner with 403 so non-admins see "forbidden" errors if they
 * navigate here directly.
 *
 * Visual rules: monochrome chrome per ADR-057, status uses the standard
 * traffic-light tokens (green / yellow / red).
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, Link as LinkIcon, RefreshCcw } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { telegramApi, telegramKeys } from '../../../lib/api/telegram';
import { cn } from '../../../lib/utils';

type FlashTone = 'success' | 'error';
interface Flash {
  tone: FlashTone;
  message: string;
}

function TelegramSettingsPage() {
  const { t } = useTranslation();
  const [token, setToken] = React.useState('');
  const [founderId, setFounderId] = React.useState('');
  const [flash, setFlash] = React.useState<Flash | null>(null);

  const statusQ = useQuery({
    queryKey: telegramKeys.status(),
    queryFn: telegramApi.status,
    refetchOnWindowFocus: true,
  });

  const testM = useMutation({
    mutationFn: (botToken: string) => telegramApi.testConnection(botToken),
    onSuccess: (data) => {
      if (data.ok) {
        setFlash({
          tone: 'success',
          message: t('settings.telegram.test_ok', {
            username: data.botUsername ?? 'bot',
          }),
        });
      } else {
        setFlash({
          tone: 'error',
          message: data.message ?? t('settings.telegram.test_failed'),
        });
      }
    },
    onError: () => {
      setFlash({ tone: 'error', message: t('settings.telegram.test_failed') });
    },
  });

  const registerM = useMutation({
    mutationFn: () => telegramApi.registerWebhook({ botToken: token, founderUserId: founderId }),
    onSuccess: async (data) => {
      if (data.ok) {
        setFlash({
          tone: 'success',
          message: data.message ?? t('settings.telegram.register_ok'),
        });
        setToken('');
        await statusQ.refetch();
      } else {
        setFlash({
          tone: 'error',
          message: data.message ?? t('settings.telegram.register_failed'),
        });
      }
    },
    onError: () => {
      setFlash({ tone: 'error', message: t('settings.telegram.register_failed') });
    },
  });

  const disconnectM = useMutation({
    mutationFn: () => telegramApi.disconnect(),
    onSuccess: async () => {
      setFlash({ tone: 'success', message: t('settings.telegram.disconnect_ok') });
      setToken('');
      setFounderId('');
      await statusQ.refetch();
    },
  });

  const status = statusQ.data;
  const configured = !!status?.configured;

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-6">
      <header>
        <h1 className="text-[15px] font-semibold tracking-tight text-foreground">
          {t('settings.telegram.title')}
        </h1>
        <p className="mt-1 text-[12px] text-muted-foreground">{t('settings.telegram.subtitle')}</p>
      </header>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <div className="flex flex-col gap-0.5">
            <CardTitle className="text-[13px] font-semibold">
              {t('settings.telegram.section_status')}
            </CardTitle>
            <CardDescription className="text-[12px]">
              {configured
                ? t('settings.telegram.status_configured')
                : t('settings.telegram.status_not_configured')}
            </CardDescription>
          </div>
          <span
            className={cn(
              'inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
              configured
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                : 'border-border bg-muted text-muted-foreground',
            )}
          >
            {configured
              ? t('settings.telegram.status_configured')
              : t('settings.telegram.status_not_configured')}
          </span>
        </CardHeader>
        <CardContent className="space-y-2 text-[12px]">
          {statusQ.isLoading && <p className="text-muted-foreground">Loading…</p>}
          {status?.error && (
            <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5" aria-hidden />
              <span>{status.error}</span>
            </p>
          )}
          {status?.message && (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-muted-foreground">
              {status.message}
            </p>
          )}
          {configured && (
            <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-1.5">
              <dt className="text-muted-foreground">{t('settings.telegram.bot_label')}</dt>
              <dd className="font-medium text-foreground">
                {status.botUsername ? `@${status.botUsername}` : '—'}
                {status.botId !== null && status.botId !== undefined && (
                  <span className="ml-1 text-muted-foreground"> (id {status.botId})</span>
                )}
              </dd>
              <dt className="text-muted-foreground">{t('settings.telegram.webhook_label')}</dt>
              <dd className="truncate font-mono text-[11px] text-foreground">
                {status.webhookUrl ?? '—'}
              </dd>
              <dt className="text-muted-foreground">{t('settings.telegram.founder_ids_label')}</dt>
              <dd className="font-mono text-[11px] text-foreground">
                {status.founderUserIds.length === 0
                  ? t('settings.telegram.no_founder_ids')
                  : status.founderUserIds.join(', ')}
              </dd>
            </dl>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => statusQ.refetch()}
              disabled={statusQ.isFetching}
            >
              <RefreshCcw aria-hidden />
              Refresh
            </Button>
            {configured && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => disconnectM.mutate()}
                disabled={disconnectM.isPending}
              >
                {disconnectM.isPending
                  ? t('settings.telegram.disconnecting')
                  : t('settings.telegram.disconnect_btn')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[13px] font-semibold">
            {t('settings.telegram.section_setup')}
          </CardTitle>
          <CardDescription className="text-[12px]">
            {t('settings.telegram.token_help')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {flash && (
            <p
              role={flash.tone === 'error' ? 'alert' : 'status'}
              className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 text-[12px]',
                flash.tone === 'error'
                  ? 'border-destructive/40 bg-destructive/10 text-destructive'
                  : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
              )}
            >
              {flash.tone === 'error' ? (
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5" aria-hidden />
              ) : (
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5" aria-hidden />
              )}
              <span>{flash.message}</span>
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="tg-token" className="text-[12px]">
              {t('settings.telegram.token_label')}
            </Label>
            <Input
              id="tg-token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={t('settings.telegram.token_placeholder')}
              autoComplete="off"
              spellCheck={false}
              className="font-mono text-[12px]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tg-founder" className="text-[12px]">
              {t('settings.telegram.founder_label')}
            </Label>
            <Input
              id="tg-founder"
              type="text"
              value={founderId}
              onChange={(e) => setFounderId(e.target.value)}
              placeholder={t('settings.telegram.founder_placeholder')}
              autoComplete="off"
              spellCheck={false}
              inputMode="numeric"
              className="font-mono text-[12px]"
            />
            <p className="text-[11px] text-muted-foreground">
              {t('settings.telegram.founder_help')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => token && testM.mutate(token)}
              disabled={!token || testM.isPending}
            >
              {testM.isPending ? t('settings.telegram.testing') : t('settings.telegram.test_btn')}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => registerM.mutate()}
              disabled={!token || !founderId || registerM.isPending}
            >
              <LinkIcon aria-hidden />
              {registerM.isPending
                ? t('settings.telegram.registering')
                : t('settings.telegram.register_btn')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/settings/telegram')({
  component: TelegramSettingsPage,
});
